import type {
  GoldenEngajamento,
  GoldenFrescor,
  GoldenFunil,
} from "@/hooks/useFreelancerTimelineCompleta";
import { formatEventoQuando } from "@/components/timeline/timelineEventUtils";

/** Taxas podem chegar como fração (0..1) ou percentual (0..100). Normaliza pra exibição. */
function percentTexto(valor: number | null | undefined): string | null {
  if (valor === null || valor === undefined) return null;
  const percentual = valor <= 1 ? valor * 100 : valor;
  return `${Math.round(percentual)}%`;
}

function numero(valor: number | null | undefined): number {
  return typeof valor === "number" ? valor : 0;
}

function CardResumo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <h3 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {titulo}
      </h3>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}

function LinhaResumo({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-muted-foreground">{rotulo}</span>
      <span className="text-right font-medium tabular-nums">{valor}</span>
    </div>
  );
}

interface TimelineResumoCardsProps {
  funil: GoldenFunil | null;
  frescor: GoldenFrescor | null;
  engajamento: GoldenEngajamento | null;
}

/**
 * Cards compactos com os números de vida do freelancer (funil de vagas,
 * atualização de cadastro e contato/campanhas). Copy pt-BR sem jargão.
 */
export function TimelineResumoCards({ funil, frescor, engajamento }: TimelineResumoCardsProps) {
  if (!funil && !frescor && !engajamento) return null;

  const conversao = percentTexto(funil?.taxa_conversao_pessoal);
  const taxaResposta = percentTexto(engajamento?.taxa_resposta);

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {funil && (
        <CardResumo titulo="Vagas e seleções">
          <LinhaResumo rotulo="Entrou em seleção" valor={numero(funil.vezes_selecionado)} />
          <LinhaResumo rotulo="Chegou à shortlist" valor={numero(funil.vezes_shortlistado)} />
          <LinhaResumo rotulo="Aprovado" valor={numero(funil.vezes_aprovado)} />
          <LinhaResumo
            rotulo="Vaga ficou com outra pessoa"
            valor={numero(funil.vezes_preterido)}
          />
          <LinhaResumo rotulo="Não seguiu no processo" valor={numero(funil.vezes_reprovado)} />
          {conversao && <LinhaResumo rotulo="Conversão pessoal" valor={conversao} />}
          {funil.ultima_vaga_em && (
            <LinhaResumo rotulo="Última vaga" valor={formatEventoQuando(funil.ultima_vaga_em)} />
          )}
        </CardResumo>
      )}

      {frescor && (
        <CardResumo titulo="Cadastro">
          <LinhaResumo
            rotulo="Última atualização"
            valor={
              frescor.ultima_atualizacao_geral
                ? formatEventoQuando(frescor.ultima_atualizacao_geral)
                : "sem registro"
            }
          />
          <LinhaResumo rotulo="Atualizações em 12 meses" valor={numero(frescor.atualizacoes_12m)} />
          {frescor.ultima_campanha_enviada_em && (
            <LinhaResumo
              rotulo="Última campanha enviada"
              valor={formatEventoQuando(frescor.ultima_campanha_enviada_em)}
            />
          )}
          {frescor.respondeu_ultima_campanha !== null &&
            frescor.respondeu_ultima_campanha !== undefined && (
              <LinhaResumo
                rotulo="Respondeu a última campanha"
                valor={frescor.respondeu_ultima_campanha ? "sim" : "não"}
              />
            )}
        </CardResumo>
      )}

      {engajamento && (
        <CardResumo titulo="Contato e campanhas">
          <LinhaResumo
            rotulo="Último contato"
            valor={
              engajamento.ultimo_contato_em
                ? formatEventoQuando(engajamento.ultimo_contato_em)
                : "sem registro"
            }
          />
          <LinhaResumo rotulo="Campanhas enviadas" valor={numero(engajamento.campanhas_enviadas)} />
          <LinhaResumo rotulo="Cliques" valor={numero(engajamento.campanhas_clicadas)} />
          <LinhaResumo rotulo="Respostas" valor={numero(engajamento.campanhas_respondidas)} />
          {taxaResposta && <LinhaResumo rotulo="Taxa de resposta" valor={taxaResposta} />}
          {engajamento.ultimo_termometro && (
            <LinhaResumo
              rotulo="Último termômetro"
              valor={
                <span>
                  {engajamento.ultimo_termometro}
                  {engajamento.ultimo_termometro_em && (
                    <span className="text-xs font-normal text-muted-foreground">
                      {" "}
                      ({formatEventoQuando(engajamento.ultimo_termometro_em)})
                    </span>
                  )}
                </span>
              }
            />
          )}
        </CardResumo>
      )}
    </div>
  );
}

export default TimelineResumoCards;
